/**
 * Fan-side WebSocket client for the `live-operator-takeover` capability.
 *
 * Opens a WS to `VITE_LIVE_WS_BASE_URL?token=<fanJwt>&kind=fan&characterId=<id>`
 * on chat mount and listens for `type:'reply'` events the server pushes
 * when an operator intervenes. The reply is delivered to the caller via
 * `onReply(text, turnId)` — the caller (EmbedRuntime) then runs it
 * through the existing reply-render pipeline so chat bubble + TTS +
 * lipsync behave identically to an LLM reply.
 *
 * Reconnect ladder: 1s / 2s / 4s / 8s / 30s. Pings every 30s as a
 * keep-alive against APIGW's 10-min idle timeout.
 *
 * Caller usage:
 *   const client = new LiveWsClient({
 *     baseUrl, token, characterId,
 *     onReply: (text, turnId) => handleIncomingReply(text, { turnId }),
 *   });
 *   client.start();
 *   // ...
 *   client.stop();
 */

export interface LiveWsClientOptions {
  /** wss:// base URL — `VITE_LIVE_WS_BASE_URL` from .env.production. */
  baseUrl: string;
  /** Fan Cognito id-token. Sent on `?token=` because browsers strip
   *  Authorization headers from the WS handshake. */
  token: string;
  /** Character whose chat session this fan is currently in. The
   *  server's intervene push uses (fanId, characterId) to find this
   *  connection's connectionId. */
  characterId: string;
  /** Fires for any server `{type:'reply'}` message — usually a single
   *  text payload + turnId from an operator override. `voice` mirrors
   *  the operator's 文字+語音 / 純文字 choice (default true). */
  onReply: (text: string, turnId: string, voice?: boolean) => void;
  /** Optional — fired on every state change for diagnostics UI. */
  onStateChange?: (state: 'connecting' | 'open' | 'closed') => void;
  /** Optional — fires after the WS handshake completes. `lastTurnId`
   *  is the highest turnId the client has seen, or null on first
   *  connect. Caller uses this to GET `/api/fan/.../history?since=…`
   *  and dispatch any missed operator overrides. */
  onOpen?: (lastTurnId: string | null) => void;
  /** Persisted cursor from a previous page session. */
  initialLastTurnId?: string | null;
}

const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 30_000];
const PING_INTERVAL_MS = 30_000;

interface ServerEnvelope {
  type?: string;
  text?: string;
  turnId?: string;
  source?: string;
  voice?: boolean;
  code?: string;
  message?: string;
}

export class LiveWsClient {
  private opts: LiveWsClientOptions;
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  /** Highest turnId seen so the caller can run a catch-up GET on
   *  reconnect (Section 4.4 — implemented later). Exposed via getter. */
  private lastSeenTurnId: string | null = null;

  constructor(opts: LiveWsClientOptions) {
    this.opts = opts;
    this.lastSeenTurnId = opts.initialLastTurnId ?? null;
  }

  start(): void {
    if (!this.opts.baseUrl) {
      console.warn('[live-ws] no baseUrl configured; skipping');
      return;
    }
    if (!this.opts.token) {
      console.warn('[live-ws] no fan token; skipping');
      return;
    }
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(1000, 'unmount'); } catch { /* noop */ }
      this.ws = null;
    }
    this.opts.onStateChange?.('closed');
  }

  /** Most recent reply turnId — caller uses this for reconnect catch-up. */
  get lastTurnId(): string | null {
    return this.lastSeenTurnId;
  }

  /** Advance the reconnect cursor after a history catch-up replay. */
  markSeen(turnId: string): void {
    if (!turnId) return;
    if (!this.lastSeenTurnId || turnId > this.lastSeenTurnId) this.lastSeenTurnId = turnId;
  }

  private open(): void {
    if (this.stopped) return;
    this.opts.onStateChange?.('connecting');
    const url =
      `${this.opts.baseUrl}` +
      `?token=${encodeURIComponent(this.opts.token)}` +
      `&kind=fan` +
      `&characterId=${encodeURIComponent(this.opts.characterId)}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      console.warn('[live-ws] WebSocket constructor threw', err);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.opts.onStateChange?.('open');
      // Start ping watchdog. APIGW WebSocket idle timeout is 10 min;
      // a ping every 30s keeps the connection warm without flooding.
      this.pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send(JSON.stringify({ action: 'ping' })); } catch { /* noop */ }
        }
      }, PING_INTERVAL_MS);
      // Notify caller — used for reconnect catch-up GET.
      try {
        this.opts.onOpen?.(this.lastSeenTurnId);
      } catch (err) {
        console.warn('[live-ws] onOpen callback threw', err);
      }
    };

    ws.onmessage = (msg) => {
      let env: ServerEnvelope;
      try {
        env = JSON.parse(msg.data as string) as ServerEnvelope;
      } catch (err) {
        console.warn('[live-ws] non-JSON message', { data: msg.data, err });
        return;
      }
      if (env.type === 'reply' && typeof env.text === 'string' && typeof env.turnId === 'string') {
        this.markSeen(env.turnId);
        try {
          this.opts.onReply(env.text, env.turnId, env.voice !== false);
        } catch (err) {
          console.warn('[live-ws] onReply callback threw', err);
        }
      } else if (env.type === 'error') {
        console.warn('[live-ws] server-emitted error', { code: env.code, message: env.message });
      }
      // Silently ignore other event types (subscribe-ack, ping-pong, …).
    };

    ws.onerror = (ev) => {
      console.warn('[live-ws] transport error', ev);
    };

    ws.onclose = () => {
      this.ws = null;
      this.opts.onStateChange?.('closed');
      if (this.pingTimer) {
        clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
      if (!this.stopped) {
        this.scheduleReconnect();
      }
    };
  }

  private scheduleReconnect(): void {
    const attempt = this.reconnectAttempts++;
    const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]!;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }
}
